import { ActivityIndicator, FlatList, Pressable, View, useDripsyTheme } from 'dripsy';
import { t } from 'i18next';
import React, { useState } from 'react';

import { keyGenerator } from '@/utils/keyGenerator';
import { Icon } from '@/design-system/components/icons/Icon';
import { UniInput } from '@/design-system/components/inputs';
import { useNativeModalView } from '@/design-system/components/modal/nativeModal/useNativeModalView';
import { TextBody, TextCaption, TextSmall, TextTitle } from '@/design-system/components/typography';
import { PageHeaderModalNative } from '@/features/account-management/screens/page/PageHeaderModalNative';
import { UniIconButton } from '@/design-system/components/buttons';
import { useCardAccounts } from '@/features/card-account/hooks/useCardAccounts.hook';
import { CardAccountTypeFilters } from '@/features/card-account/options';
import { CardAccountTypeColor } from '@/features/card-account/enum';
import { formatAmountByDecimals } from '@/utils/numberFormatting';
import { debounce } from 'lodash';

export interface SelectCardAccountNativeScreenProps {
  onSelect: (data: any) => void;
}

export const SelectCardAccountNativeScreen: React.FC<SelectCardAccountNativeScreenProps> = (props) => {
  const { onSelect } = props;
  const { theme } = useDripsyTheme();

  const { handleBack, animatedStyle } = useNativeModalView({});

  const onSelectWallet = (data: any) => {
    onSelect(data);
    handleBack();
  };

  const [search, setSearch] = useState('');
  const { accounts, isLoading } = useCardAccounts({ pageSize: 10, search });

  return (
    <View sx={{ backgroundColor: '$backgroundPrimary', width: '100%', height: '100%' }}>
      <PageHeaderModalNative>
        <View sx={{ px: '$4', my: '$5' }}>
          <View sx={{ position: 'absolute', top: 0, left: 12, zIndex: 2 }}>
            <UniIconButton icon="arrowLeft" options={{ iconStyles: { themeColor: '$white' } }} onPress={() => handleBack()}></UniIconButton>
          </View>
          <View sx={{ textAlign: 'center', width: '100%' }}>
            <TextTitle bold sx={{ color: '$white' }}>
              {t('earn-invest::glossary.select-account')}
            </TextTitle>
          </View>
          <View sx={{ position: 'absolute', top: 0, right: 12, zIndex: 2 }}></View>
        </View>
      </PageHeaderModalNative>
      <View sx={{ paddingX: '$4', paddingY: '$3' }}>
        <UniInput
          prefixIcon="search"
          wrapperSx={{ flex: 1 }}
          sx={{ width: '100%' }}
          onValueChange={debounce((value) => setSearch(value || ''), 1000)}
          placeholder={t('card-account::credit-card-new-account.glossary.search-by-name')}
        />
      </View>
      <View sx={{ height: 'auto' }}>
        {!isLoading ? (
          accounts.length > 0 ? (
            <FlatList
              data={accounts}
              keyExtractor={(item) => keyGenerator('accountSelector')}
              renderItem={({ item }) => (
                <Pressable
                  sx={{
                    paddingX: '$4',
                    paddingY: '$2',
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    backgroundColor: 'transparent'
                  }}
                  onPress={() => onSelectWallet(item)}>
                  <View sx={{ pt: '$3', pb: '$1', width: '100%', gap: '$1' }}>
                    <View sx={{ flexDirection: 'row', justifyContent: 'space-between' }}>
                      <TextSmall semiBold sx={{ color: '$secondary' }}>
                        {item?.label || ' - '}
                      </TextSmall>
                      <TextSmall semiBold sx={{ color: '$secondary' }}>
                        {formatAmountByDecimals({ value: Math.abs(item?.balance), suffix: item?.assetSymbol })}{' '}
                        {item?.balance < 0 && '[CR]'}
                      </TextSmall>
                    </View>
                    <View sx={{ flexDirection: 'row', gap: '$4', alignItems: 'center' }}>
                      {item?.autopayEnabled && (
                        <View
                          sx={{
                            paddingX: '$2',
                            paddingY: '$1',
                            width: 'fit-content',
                            borderRadius: 100,
                            backgroundColor: theme.colors?.$tagGreenBackground as string
                          }}>
                          <TextCaption sx={{ color: theme.colors?.$tagGreenText as string }}>
                            {t('card-account::landing-page.text--utgl-autopay-enabled')}
                          </TextCaption>
                        </View>
                      )}
                      <View
                        sx={{
                          paddingX: '$2',
                          paddingY: '$1',
                          width: 'fit-content',
                          borderRadius: 100,
                          backgroundColor: theme.colors?.$tagBlackBackground as string
                        }}>
                        <TextCaption sx={{ color: theme.colors?.$tagBlackText as string }}>{item?.assetSymbol}</TextCaption>
                      </View>
                      <View
                        sx={{
                          paddingX: '$2',
                          paddingY: '$1',
                          width: 'fit-content',
                          borderRadius: 100,
                          backgroundColor: theme.colors[`$tag${CardAccountTypeColor[item?.type as ColorTypes]}Background`] as string
                        }}>
                        <TextCaption sx={{ color: theme.colors[`$tag${CardAccountTypeColor[item?.type as ColorTypes]}Text`] as string }}>
                          {CardAccountTypeFilters(t).find((rec) => rec.value === item?.type)?.label}
                        </TextCaption>
                      </View>
                    </View>
                  </View>
                </Pressable>
              )}
            />
          ) : (
            <View sx={{ flex: 1, height: '100%', paddingX: '$4', paddingY: '$3', justifyContent: 'center', alignItems: 'center' }}>
              <Icon name="listZeroState" height={60} width={60} />
              <TextBody bold sx={{ textAlign: 'center', marginTop: '$3' }}>
                {t('earn-invest::glossary.no-accounts-found')}
              </TextBody>
            </View>
          )
        ) : (
          <View sx={{ padding: '$5' }}>
            <ActivityIndicator size="large" color={(theme.colors?.$disable as string) || '#F0F0F0'} />
          </View>
        )}
      </View>
    </View>
  );
};
